import React, { useState } from 'react';
import { Activity, ArrowLeft, ShieldAlert } from 'lucide-react';
import { AppView } from '../../types';

interface HealthWellnessCalculatorProps {
  onNavigateToView: (view: AppView) => void;
  defaultTab?: 'bmi' | 'calories';
}

export const HealthWellnessCalculator: React.FC<HealthWellnessCalculatorProps> = ({ onNavigateToView, defaultTab = 'bmi' }) => {
  const [subTab, setSubTab] = useState<'bmi' | 'calories'>(defaultTab);

  // BMI State
  const [weightKg, setWeightKg] = useState<string>('80');
  const [heightCm, setHeightCm] = useState<string>('180');

  // Calorie State
  const [age, setAge] = useState<string>('30');
  const [gender, setGender] = useState<'male' | 'female'>('male');
  const [activityLevel, setActivityLevel] = useState<string>('1.375'); // Lightly active

  // BMI Calculation
  const w = parseFloat(weightKg) || 0;
  const hM = (parseFloat(heightCm) || 1) / 100;
  const bmiVal = hM > 0 ? w / (hM * hM) : 0;

  let bmiCategory = 'Poids normal';
  let bmiColor = 'text-emerald-600';
  if (bmiVal < 18.5) {
    bmiCategory = 'Insuffisance pondérale (Maigreur)';
    bmiColor = 'text-sky-600';
  } else if (bmiVal >= 25 && bmiVal < 30) {
    bmiCategory = 'Surpoids';
    bmiColor = 'text-amber-600';
  } else if (bmiVal >= 30) {
    bmiCategory = 'Obésité';
    bmiColor = 'text-rose-600';
  }

  // Calorie Calculation (Mifflin-St Jeor equation)
  const a = parseFloat(age) || 30;
  let bmr = (10 * w) + (6.25 * (parseFloat(heightCm) || 170)) - (5 * a);
  bmr = gender === 'male' ? bmr + 5 : bmr - 161;
  const tdee = bmr * (parseFloat(activityLevel) || 1.2);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] pb-16">
      <div className="bg-white border-b border-[#E2E8F0] px-6 py-6">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => onNavigateToView('everyday-command-center')}
              className="p-2 bg-[#F1F5F9] hover:bg-[#E2E8F0] rounded-xl text-[#0F172A] transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">
                YamooPay Health & Wellness Calculators (Prompt 14)
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-[#0F172A]">
                Calculateur d'IMC & Estimation Énergétique Quotidienne
              </h1>
            </div>
          </div>
          <button
            onClick={() => onNavigateToView('everyday-command-center')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-xs font-bold transition-colors shadow-xs cursor-pointer"
          >
            Centre de Commande
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 mt-8">
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-8 flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900">
            <strong>Avertissement important :</strong> Ces calculateurs (IMC et calories) sont purement informatifs et ne constituent en aucun cas un diagnostic médical ou une prescription nutritionnelle. Les données de santé saisies sont strictement isolées et ne sont jamais transmises aux services publicitaires ou analytiques.
          </div>
        </div>

        <div className="flex border-b border-[#E2E8F0] gap-6 mb-8">
          {[
            { id: 'bmi', label: 'Calculateur d\'IMC Adulte' },
            { id: 'calories', label: 'Estimation Énergétique (BMR & TDEE)' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSubTab(tab.id as any)}
              className={`pb-3 text-xs font-bold border-b-2 transition-colors cursor-pointer ${
                subTab === tab.id
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-[#64748B] hover:text-[#0F172A]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {subTab === 'bmi' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2 bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-xs">
              <h3 className="text-base font-bold text-[#0F172A] mb-4">Morphologie (Adultes uniquement)</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Poids (kg)</label>
                  <input
                    type="number"
                    value={weightKg}
                    onChange={(e) => setWeightKg(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-emerald-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Taille (cm)</label>
                  <input
                    type="number"
                    value={heightCm}
                    onChange={(e) => setHeightCm(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-emerald-600"
                  />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-emerald-900 to-slate-900 text-white rounded-2xl p-6 flex flex-col justify-between shadow-md">
              <div>
                <div className="text-xs font-bold text-emerald-300 uppercase tracking-wider mb-2">Votre IMC Estimé</div>
                <div className="text-4xl font-extrabold tracking-tight mt-1">
                  {bmiVal.toFixed(2)}
                </div>
                <div className={`mt-3 text-sm font-bold ${bmiColor}`}>
                  {bmiCategory}
                </div>
              </div>
              <div className="mt-8 pt-4 border-t border-white/10 text-[11px] text-slate-300">
                Indice de Masse Corporelle (OMS). Ne mesure pas directement la masse grasse.
              </div>
            </div>
          </div>
        )}

        {subTab === 'calories' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2 bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-xs">
              <h3 className="text-base font-bold text-[#0F172A] mb-4">Équation de Mifflin-St Jeor</h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Âge (années)</label>
                  <input
                    type="number"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-emerald-600"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Genre</label>
                  <select
                    value={gender}
                    onChange={(e) => setGender(e.target.value as any)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-emerald-600"
                  >
                    <option value="male">Homme</option>
                    <option value="female">Femme</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#475569] mb-1">Niveau d'Activité</label>
                  <select
                    value={activityLevel}
                    onChange={(e) => setActivityLevel(e.target.value)}
                    className="w-full bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl px-4 py-2.5 text-sm font-semibold text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-emerald-600"
                  >
                    <option value="1.2">Sédentaire (peu ou pas d'exercice)</option>
                    <option value="1.375">Légèrement actif (exercice léger 1-3j/sem)</option>
                    <option value="1.55">Modérément actif (exercice modéré 3-5j/sem)</option>
                    <option value="1.725">Très actif (exercice intense 6-7j/sem)</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-emerald-900 to-slate-900 text-white rounded-2xl p-6 flex flex-col justify-between shadow-md">
              <div>
                <div className="text-xs font-bold text-emerald-300 uppercase tracking-wider mb-2">Bilan Énergétique</div>
                <div className="space-y-4 mt-4">
                  <div>
                    <div className="text-xs text-emerald-200">Métabolisme Basal (BMR)</div>
                    <div className="text-2xl font-bold">{Math.round(bmr)} kcal/jour</div>
                  </div>
                  <div className="pt-3 border-t border-white/10">
                    <div className="text-xs text-emerald-200">Dépense Quotidienne Totale (TDEE)</div>
                    <div className="text-3xl font-extrabold text-emerald-300">{Math.round(tdee)} kcal/jour</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
